/**
 * AdminToast — Swal-compatible notification shim.
 *
 * Sets window.Swal so every existing Swal.fire() / Swal.mixin() call in
 * product-index, product-form, product-categories-*, section-index,
 * attribute-groups-index, attribute-values-index, product-shipping,
 * product-reviews, product-related and the layout session-flash handler
 * all keep working without any code changes in those files.
 *
 * Behaviour map:
 *  toast:true               → lightweight slide-in toast (top-right)
 *  showCancelButton:true    → custom confirm dialog (Promise → {isConfirmed})
 *  imageUrl                 → image lightbox overlay
 *  allowOutsideClick:false  → loading spinner overlay
 *  everything else          → toast (converts blocking info/error dialogs)
 */
(function (global) {
    'use strict';

    /* ── DOM containers (created lazily) ────────────────────── */
    var _root, _confirmEl, _imgEl, _loadingEl;
    var _lastProgressBar = null;

    function boot() {
        _root = document.createElement('div');
        _root.id = '__adm-toast-root';
        document.body.appendChild(_root);
    }

    /* ── Icon map ───────────────────────────────────────────── */
    var RI = {
        success:  'ri-checkbox-circle-fill',
        error:    'ri-close-circle-fill',
        warning:  'ri-error-warning-fill',
        info:     'ri-information-fill',
        question: 'ri-question-fill',
    };

    /* Confirm button colour → CSS class */
    var BTN_MAP = {
        '#dc2626':'danger','#d33':'danger','#e11d48':'danger',
        '#dc3545':'danger','#ef4444':'danger','#c82333':'danger',
        '#f59e0b':'warning','#d97706':'warning',
        '#3085d6':'info',  '#0ea5e9':'teal',
        '#4f46e5':'primary','#4361ee':'primary','#0d6efd':'primary',
    };
    function btnClass(hex) {
        if (!hex) return 'primary';
        return BTN_MAP[(hex + '').toLowerCase()] || 'primary';
    }

    var ICON_TYPE = {
        success:'success', error:'danger', warning:'warning', info:'info', question:'warning',
    };
    function iconWrapClass(icon) {
        return 'adm-confirm__icon-wrap--' + (ICON_TYPE[icon] || 'warning');
    }

    /* ══════════════════════════════════════════════════════════
       TOAST
    ══════════════════════════════════════════════════════════ */
    function showToast(opts) {
        if (!_root) boot();

        var icon    = opts.icon  || 'info';
        var title   = opts.title || '';
        var text    = opts.text  || '';
        var timer   = opts.timer != null ? +opts.timer : 3500;
        var showBar = opts.timerProgressBar !== false && timer > 0;

        var el  = document.createElement('div');
        el.className = 'adm-toast adm-toast--' + icon;
        el.innerHTML =
            '<div class="adm-toast__icon"><i class="' + (RI[icon] || RI.info) + '"></i></div>' +
            '<div class="adm-toast__body">' +
                (title ? '<div class="adm-toast__title">' + title + '</div>' : '') +
                (text  ? '<div class="adm-toast__text">'  + text  + '</div>' : '') +
            '</div>' +
            '<button class="adm-toast__close" aria-label="Close"><i class="ri-close-line"></i></button>' +
            (showBar ? '<div class="adm-toast__progress" style="animation-duration:' + timer + 'ms;"></div>' : '');

        var bar  = el.querySelector('.adm-toast__progress');
        _lastProgressBar = bar || null;

        el.querySelector('.adm-toast__close')
          .addEventListener('click', function () { dismiss(el); });

        _root.insertBefore(el, _root.firstChild);

        /* Call didOpen with the element (used by layout for hover timer) */
        if (typeof opts.didOpen === 'function') opts.didOpen(el);

        /* Auto-dismiss + hover-pause */
        var tid;
        function startTimer() {
            if (timer > 0) tid = setTimeout(function () { dismiss(el); }, timer);
        }
        function stopTick() {
            clearTimeout(tid);
            if (bar) bar.style.animationPlayState = 'paused';
        }
        function resumeTick() {
            if (bar) bar.style.animationPlayState = 'running';
            startTimer();
        }

        el.addEventListener('mouseenter', stopTick);
        el.addEventListener('mouseleave', resumeTick);
        startTimer();

        return el;
    }

    function dismiss(el) {
        if (!el || el._out) return;
        el._out = true;
        el.removeEventListener('mouseenter', el._stop);
        el.removeEventListener('mouseleave', el._resume);
        el.classList.add('is-leaving');
        setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 320);
    }

    /* ══════════════════════════════════════════════════════════
       CONFIRM DIALOG
    ══════════════════════════════════════════════════════════ */
    function showConfirm(opts) {
        return new Promise(function (resolve) {
            if (!_confirmEl) {
                _confirmEl = document.createElement('div');
                _confirmEl.className = 'adm-confirm-overlay is-hidden';
                document.body.appendChild(_confirmEl);
            }

            var icon        = opts.icon || 'warning';
            var title       = opts.title || 'Are you sure?';
            var html        = opts.html  || (opts.text ? '<p>' + opts.text + '</p>' : '');
            var confirmText = opts.confirmButtonText || 'Confirm';
            var cancelText  = opts.cancelButtonText  || 'Cancel';
            var okClass     = 'adm-confirm__btn--' + btnClass(opts.confirmButtonColor);

            _confirmEl.innerHTML =
                '<div class="adm-confirm">' +
                    '<div class="adm-confirm__icon-wrap ' + iconWrapClass(icon) + '">' +
                        '<i class="' + (RI[icon] || RI.warning) + '"></i>' +
                    '</div>' +
                    '<div class="adm-confirm__title">' + title + '</div>' +
                    (html ? '<div class="adm-confirm__html">' + html + '</div>' : '') +
                    '<div class="adm-confirm__btns">' +
                        '<button class="adm-confirm__btn adm-confirm__btn--cancel">' + cancelText + '</button>' +
                        '<button class="adm-confirm__btn ' + okClass + ' js-confirm-ok">' + confirmText + '</button>' +
                    '</div>' +
                '</div>';

            _confirmEl.classList.remove('is-hidden');
            document.body.style.overflow = 'hidden';

            function close(confirmed) {
                _confirmEl.classList.add('is-hidden');
                document.body.style.overflow = '';
                document.removeEventListener('keydown', onKey);
                resolve({ isConfirmed: confirmed, isDismissed: !confirmed, value: confirmed ? true : undefined });
            }

            _confirmEl.querySelector('.adm-confirm__btn--cancel')
                .addEventListener('click', function () { close(false); });
            _confirmEl.querySelector('.js-confirm-ok')
                .addEventListener('click', function () { close(true); });
            _confirmEl.addEventListener('click', function (e) {
                if (e.target === _confirmEl) close(false);
            });

            function onKey(e) { if (e.key === 'Escape') close(false); }
            document.addEventListener('keydown', onKey);
        });
    }

    /* ══════════════════════════════════════════════════════════
       IMAGE LIGHTBOX
    ══════════════════════════════════════════════════════════ */
    function showImage(opts) {
        if (!_imgEl) {
            _imgEl = document.createElement('div');
            _imgEl.className = 'adm-img-overlay is-hidden';
            _imgEl.innerHTML =
                '<button class="adm-img-close"><i class="ri-close-line"></i></button>' +
                '<img class="adm-img-photo" src="" alt="">';
            var closeBtn = _imgEl.querySelector('.adm-img-close');
            closeBtn.addEventListener('click', function () { _imgEl.classList.add('is-hidden'); });
            _imgEl.addEventListener('click', function (e) {
                if (e.target === _imgEl) _imgEl.classList.add('is-hidden');
            });
            document.body.appendChild(_imgEl);
        }
        _imgEl.querySelector('.adm-img-photo').src = opts.imageUrl || '';
        _imgEl.querySelector('.adm-img-photo').alt = opts.imageAlt || '';
        _imgEl.classList.remove('is-hidden');
        return Promise.resolve({ isConfirmed: false, isDismissed: true });
    }

    /* ══════════════════════════════════════════════════════════
       LOADING OVERLAY
    ══════════════════════════════════════════════════════════ */
    function showLoadingOverlay(opts) {
        if (!_loadingEl) {
            _loadingEl = document.createElement('div');
            _loadingEl.className = 'adm-loading-overlay is-hidden';
            _loadingEl.innerHTML =
                '<div class="adm-loading-box">' +
                    '<div class="adm-loading-spinner"></div>' +
                    '<div class="adm-loading-title"></div>' +
                    '<div class="adm-loading-text"></div>' +
                '</div>';
            document.body.appendChild(_loadingEl);
        }
        _loadingEl.querySelector('.adm-loading-title').textContent = opts.title || '';
        _loadingEl.querySelector('.adm-loading-text').innerHTML    = opts.html  || opts.text || '';
        _loadingEl.classList.remove('is-hidden');
        if (typeof opts.didOpen === 'function') opts.didOpen(_loadingEl);
    }

    function hideLoadingOverlay() {
        if (_loadingEl) _loadingEl.classList.add('is-hidden');
    }

    /* ══════════════════════════════════════════════════════════
       MAIN fire()
    ══════════════════════════════════════════════════════════ */
    function fire(opts) {
        if (!opts) return Promise.resolve({ isConfirmed: false });

        /* Image lightbox */
        if (opts.imageUrl) return showImage(opts);

        /* Loading overlay — has no cancel, outer clicks blocked */
        if (opts.allowOutsideClick === false && !opts.showCancelButton) {
            showLoadingOverlay(opts);
            return Promise.resolve({ isConfirmed: false });
        }

        /* Confirmation dialog */
        if (opts.showCancelButton) return showConfirm(opts);

        /* Everything else → toast (converts blocking modals to non-blocking) */
        showToast(opts);
        return Promise.resolve({ isConfirmed: false, isDismissed: true });
    }

    /* ══════════════════════════════════════════════════════════
       PUBLIC Swal-compatible API
    ══════════════════════════════════════════════════════════ */
    var Swal = {
        fire: fire,

        /** Returns a factory with pre-merged defaults (used by layout flash handler) */
        mixin: function (defaults) {
            return {
                fire: function (opts) {
                    return fire(Object.assign({}, defaults, opts));
                },
            };
        },

        /* Timer control — referenced as event handlers in layout mixin didOpen */
        stopTimer:   function () { if (_lastProgressBar) _lastProgressBar.style.animationPlayState = 'paused'; },
        resumeTimer: function () { if (_lastProgressBar) _lastProgressBar.style.animationPlayState = 'running'; },

        /* Loading state helpers (called inside didOpen callbacks) */
        showLoading: function () { /* handled inside fire() */ },
        close:       hideLoadingOverlay,
        isVisible:   function () { return false; },
    };

    global.Swal = Swal;

    /* Init on DOM ready */
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }

}(window));
